import { useCallback, useRef } from "react";

import { ItemDataType, TreeNodeType, TreeNodesType } from "./types";

const attachParent = <T>(data: T, parent: T): T => {
  Object.defineProperty(data, "parent", {
    value: parent,
    writable: false,
    enumerable: false,
    configurable: true,
  });
  return data;
};

export const flattenTree = <TItem>(
  tree: TItem[],
  executor: (node: any, index: number) => any
): TItem[] => {
  const flattenData: TItem[] = [];
  const traverse = (data: any[], parent: any | null) => {
    if (!Array.isArray(data)) {
      return;
    }

    data.forEach((item: any, index: number) => {
      const node = executor(item, index);

      flattenData.push(attachParent(node, parent));

      if (item.children) {
        traverse(item.children, item);
      }
    });
  };

  traverse(tree, null);
  return flattenData;
};

export const useFlattenTreeData = () => {
  const flattenNodesBuffer = useRef<TreeNodesType>({});

  const flattenTreeData = useCallback(
    (treeData?: ItemDataType[] | null, parent?: ItemDataType, layer = 1) => {
      if (!Array.isArray(treeData) || treeData.length === 0) {
        return [];
      }

      treeData.forEach(node => {
        const { type, value } = node;

        const refKey = `${type}_${value}`;
        // eslint-disable-next-line no-param-reassign
        (node as TreeNodeType).refKey = refKey;

        const normalizedNode = node as TreeNodeType;
        const parentNode = parent
          ? ({
              type: parent.type,
              payload: parent.payload,
              refKey: (parent as TreeNodeType).refKey,
              label: parent.label,
              value: parent.value,
              databaseName: parent.databaseName,
            } as TreeNodeType)
          : undefined;

        flattenNodesBuffer.current[refKey] = {
          ...normalizedNode,
          layer,
          parent: parentNode,
        } as TreeNodeType;

        flattenTreeData(node.children, node, layer + 1);
      });
    },
    []
  );

  const getFlattenNodes = (data: ItemDataType[]) => {
    flattenNodesBuffer.current = {};
    flattenTreeData(data);
    return flattenNodesBuffer.current;
  };

  const formatVirtualizedTreeData = (
    nodes: TreeNodesType,
    data: any[],
    expandItemValues: string[],
    searchKeyword?: string,
    useInternalSearch = true
  ): TreeNodeType[] => {
    const normalizedSearch =
      useInternalSearch && searchKeyword ? searchKeyword.toLowerCase() : "";
    const expandedSet = new Set(expandItemValues);
    const filtered =
      useInternalSearch && normalizedSearch
        ? data.filter(node =>
            node.label?.toLowerCase().includes(normalizedSearch)
          )
        : data;

    return flattenTree(filtered, (node: any) => {
      const curNode = nodes?.[node.refKey];
      if (!curNode) {
        return node;
      }
      let visible = true;
      if (curNode.parent) {
        let parentNode: TreeNodeType | undefined = curNode.parent;
        while (parentNode) {
          if (!expandedSet.has(parentNode.value)) {
            visible = false;
            break;
          }
          if (!parentNode.refKey) {
            break;
          }
          parentNode = nodes[parentNode.refKey]?.parent;
        }
      }

      return {
        ...node,
        hasChildren: !!node.children,
        layer: curNode.layer,
        parent: curNode.parent,
        visible,
      };
    });
  };

  return {
    getFlattenNodes,
    flattenTreeData,
    formatVirtualizedTreeData,
  };
};
